import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { PRIVATE_COMPETITIONS } from 'utils/constants/sportsbook/private-competitions';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { streamTabOnclick } from 'utils/sportsbook/live-games-helper';
import { IGameStats } from 'interfaces/sportsbook-data-levels';
import { TMatchInfoHideGame } from 'components/MatchInfoCard';
import { usePrevious } from 'hooks/usePrevious';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import {
  setDisableMatchStreamingStreamType,
  setIsMatchStreamingPreviewUnavailable,
  setMatchStreamingStreamType
} from 'store/actions/match-streaming';
import {
  getDisableMatchStreamingType,
  getMatchStreamingType
} from 'store/selectors/match-streaming';
import { getIsLoggedIn } from 'store/selectors/user-data';

type Props = {
  game?: TMatchInfoHideGame;
};

export const useMatchInfoHide = ({
  game
}: Props): Props['game'] | undefined => {
  const { t } = useTranslation();
  const { params } = useSbRouteMatch();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const previousGameId = usePrevious(params.game);
  const [alteredGame, setAlteredGame] = useState<Props['game']>();

  const disableMatchStreamingType = useSelector(getDisableMatchStreamingType);
  const disableMatchStreamingTypeRef = useRef(disableMatchStreamingType);

  const matchStreamingType = useSelector(getMatchStreamingType);
  const matchStreamingTypeRef = useRef(matchStreamingType);

  const getPreviousView = () => {
    if (disableMatchStreamingTypeRef.current.disable) {
      dispatch(
        setDisableMatchStreamingStreamType({
          disable: false,
          previous: 0
        })
      );
      streamTabOnclick(disableMatchStreamingTypeRef.current.previous);
      dispatch(
        setMatchStreamingStreamType(
          disableMatchStreamingTypeRef.current.previous
        )
      );
    }
  };

  useEffect(() => {
    disableMatchStreamingTypeRef.current = disableMatchStreamingType;
  }, [disableMatchStreamingType]);

  useEffect(() => {
    matchStreamingTypeRef.current = matchStreamingType;
  }, [matchStreamingType]);

  useEffect(() => {
    if (
      !params.game ||
      previousGameId === params.game ||
      !Object.keys(game || {}).length ||
      isLoggedIn
    ) {
      setAlteredGame(game);
      dispatch(setIsMatchStreamingPreviewUnavailable(false));

      getPreviousView();

      return;
    }

    const privateCompetitionIds =
      PRIVATE_COMPETITIONS[SpringConfigs.JURISDICTION];

    if (
      privateCompetitionIds &&
      privateCompetitionIds.includes(Number(params.competition))
    ) {
      const gameCopy = JSON.parse(JSON.stringify(game)) as Props['game'];

      if (gameCopy) {
        gameCopy.text_info = t('sportsbook.signInStatistics');
        game?.stats &&
          Object.keys(game.stats).forEach((stat: string) => {
            gameCopy.stats[stat as keyof IGameStats] = {
              team1_value: '-',
              team2_value: '-',
              add_info: null,
              attack: {
                ...(gameCopy.stats[stat as keyof IGameStats]?.attack || {}),
                team1_value: '-',
                team2_value: '-'
              },
              dangerous_attack: {
                ...(gameCopy.stats[stat as keyof IGameStats]
                  ?.dangerous_attack || {}),
                team1_value: '-',
                team2_value: '-'
              },
              foul: {
                ...(gameCopy.stats[stat as keyof IGameStats]?.foul || {}),
                team1_value: '-',
                team2_value: '-'
              },
              aces: {
                ...(gameCopy.stats[stat as keyof IGameStats]?.aces || {}),
                team1_value: '-',
                team2_value: '-'
              },
              double_fault: {
                ...(gameCopy.stats[stat as keyof IGameStats]?.double_fault ||
                  {}),
                team1_value: '-',
                team2_value: '-'
              }
            };
          });

        if (gameCopy.info) {
          gameCopy.info.pass_team = 'none';
          gameCopy.info.score1 = '-';
          gameCopy.info.score2 = '-';
          gameCopy.info.current_game_state = '';
        }

        setAlteredGame(gameCopy);
        dispatch(setIsMatchStreamingPreviewUnavailable(true));

        !disableMatchStreamingTypeRef.current.disable &&
          dispatch(
            setDisableMatchStreamingStreamType({
              disable: true,
              previous: matchStreamingTypeRef.current
            })
          );

        streamTabOnclick(0);
        dispatch(setMatchStreamingStreamType(0));
      }
    } else {
      dispatch(setIsMatchStreamingPreviewUnavailable(false));
      setAlteredGame(game);

      getPreviousView();
    }
  }, [params, game, isLoggedIn]);

  return alteredGame;
};
